import type { UseApplyComponentActionType } from "@v1/hooks/useApplyComponentAction";
import type { DraggableEvent } from "react-draggable";
import type {
  Edges,
  NumericBounds,
  Position,
} from "replo-runtime/shared/types";
import type { BoxSide } from "replo-utils/lib/types";
import type { Component as ReploComponent } from "schemas/component";
import type { RuntimeStyleAttribute } from "schemas/styleAttribute";

import * as React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import { infoToast } from "@v1/components/designSystem/alert/Toast";
import DeprecatedIconButton from "@v1/components/designSystem/button/IconButton";
import { getComponentName } from "@v1/components/editor/component";
import {
  canRenderEditorControl,
  getEditorPropEditorData,
} from "@v1/components/editor/editorProps";
import { elementTypeToEditorData } from "@v1/components/editor/element";
import { useEditorPerformanceContext } from "@v1/contexts/editor-performance.context";
import useCurrentDragType from "@v1/hooks/useCurrentDragType";
import useDropTarget from "@v1/hooks/useDropTarget";
import { usePaintSharedStateValue } from "@v1/hooks/usePaintSharedState";
import useSetDraftElement from "@v1/hooks/useSetDraftElement";
import { useShowDraftComponentsControls } from "@v1/hooks/useShowDraftComponentsControls";
import { useV1AIStreaming } from "@v1/providers/AIStreamingProvider";
import useDragAndDrop from "@v1/providers/DragAndDropProvider";
import {
  selectCandidateCanvas,
  selectCandidateComponent,
  selectComponentIdToDrag,
  selectComponentNodeToDrag,
} from "@v1/reducers/candidate-reducer";
import {
  selectAreMultipleDraftComponents,
  selectComponentById,
  selectComponentDataMapping,
  selectComponentMapping,
  selectComponentNodes,
  selectDraftComponent,
  selectDraftComponentAccordionAncestor,
  selectDraftComponentAncestorWithEditorProps,
  selectDraftComponentAncestorWithEditorPropsNode,
  selectDraftComponentHasParent,
  selectDraftComponentId,
  selectDraftComponentIds,
  selectDraftComponentNode,
  selectDraftComponentSellingPlans,
  selectDraftComponentsNode,
  selectDraftComponentTextDynamicDataInfo,
  selectDraftComponentType,
  selectDraftElement_warningThisWillRerenderOnEveryUpdate,
  selectDraftElementId,
  selectDraftElementType,
  selectDraftNodes,
  selectDraftParentComponentNode,
  selectEditorMode,
  selectGetAttribute,
  selectHasDraftComponentAncestorOrSelfWithSpecificType,
  selectImageSource,
  selectIsDraftComponent,
  selectIsDraftComponentConnectedToDynamicData,
  selectPosition,
  selectSymbolsMapping,
  setDraftElement,
} from "@v1/reducers/core-reducer";
import { selectIsDraggingSpacingIndicators } from "@v1/reducers/drag-and-drop-reducer";
import { selectEditorBrokenMediaComponentIds } from "@v1/reducers/editor-media-reducer";
import {
  selectLeftBarActiveTab,
  setLeftBarActiveTab,
  setRightBarActiveTab,
} from "@v1/reducers/ui-reducer";
import {
  useEditorDispatch,
  useEditorSelector,
  useEditorStore,
} from "@v1/store/store";
import {
  findNextSibling,
  findPreviousSibling,
  getEdgeAttribute,
  getEditorComponentNode,
  getParentComponentFromMapping,
} from "@v1/utils/component";
import { processDropTargetDrag } from "@v1/utils/dropTarget";
import { getSelectedComponentIds } from "@v1/utils/tree-utils";
import { getElementMargin, sanitizeTreeContent } from "@v2/utils/dom";
import Draggable from "react-draggable";
import { shallowEqual } from "react-redux";
import { getSharedStateKey } from "replo-runtime/shared/state";
import { getCustomPropDefinitions } from "replo-runtime/shared/utils/component";
import { parseUnit } from "replo-runtime/shared/utils/units";
import { getRenderData } from "replo-runtime/store/components";
import { getAccordionNewItems } from "replo-runtime/store/utils/accordion";
import { isMixedStyleValue } from "replo-runtime/store/utils/mixed-values";
import { isEmpty } from "replo-utils/lib/misc";
import { useRequiredContext } from "replo-utils/react/context";
import { useMutationObserver } from "replo-utils/react/use-mutation-observer";

import {
  CONTROL_BOX_HEIGHT,
  getChildBoxRenderComponentIds,
  isEqualBoundingBox,
} from "./bounding-boxes-utils";
import { useCandidateNode, useSetCandidateNode } from "./candidateNode";
import { CANVAS_Z_INDICES, DRAGGABLE_BOUNDS } from "./canvas-constants";
import { CanvasAreaContext, CanvasContext } from "./canvas-context";
import {
  selectActiveCanvas,
  selectCanvasInteractionMode,
  selectCanvasRelativeBoundingClientRect,
  selectCanvasScale,
  setCanvasInteractionMode,
} from "./canvas-reducer";
import { scalingFactor } from "./canvas-utils";
import { SpacingIndicators } from "./SpacingIndicators";
import { useDropHandler } from "./useDropHandler";
import useSetActiveCanvas from "./useSetActiveCanvas";
// Import the CSS file for gradient border animation
import "./gradient-border.css";

import type { EditorProp } from "replo-runtime/shared/utils/editorProps";

import { DynamicDataFormatter } from "@v1/components/editor/page/DynamicDataFormatter";
import { DynamicDataSelector } from "@v1/components/editor/page/DynamicDataSelector";
import IsolatedReploComponent from "@v1/components/IsolatedReploComponent";
import useApplyComponentAction from "@v1/hooks/useApplyComponentAction";
import useDynamicDataQuickConnect from "@v1/hooks/useDynamicDataQuickConnect";
import { getComponentIdFromSlideIndex } from "@v1/utils/carousel";
import { getPathFromVariable } from "@v1/utils/dynamic-data";
import { isFeatureEnabled } from "@v2/features/infra/featureFlags";
import { Database, Hash, Settings } from "lucide-react";
import {
  doesComponentHaveConnectedDynamicDataProps,
  doesComponentSupportDynamicDataProps,
  getDynamicDataReferenceType,
} from "replo-runtime/shared/utils/dynamic-data";

import { BoundingBoxPropControls } from "./BoundingBoxPropControls";

// #region Components
export const BoundingBoxes: React.FC = () => {
  const { canvas } = useRequiredContext(CanvasContext);
  const { currentDragType } = useRequiredContext(CanvasAreaContext);
  const targetFrameOverlayRef = React.useRef<HTMLDivElement>(null);

  const store = useEditorStore();
  const dispatch = useEditorDispatch();

  const interactionMode = useEditorSelector(selectCanvasInteractionMode);
  const activeCanvas = useEditorSelector(selectActiveCanvas);
  const draftComponent = useEditorSelector(selectDraftComponent);
  const isDraggingSpacingIndicators = useEditorSelector(
    selectIsDraggingSpacingIndicators,
  );
  const draftComponentPosition = useEditorSelector(selectPosition);
  const componentIdToDrag = useEditorSelector(selectComponentIdToDrag);
  const componentNodeToDrag = useEditorSelector(selectComponentNodeToDrag);
  const editorMode = useEditorSelector(selectEditorMode);
  const candidateCanvas = useEditorSelector(selectCandidateCanvas);

  const setDraftElement = useSetDraftElement();
  const { setWasCandidateBoxJustClicked } = useEditorPerformanceContext();

  const { candidateNode, shouldShowCandidateBox } = useCandidateNode();
  const { setCandidateNodeFromEvent } = useSetCandidateNode();
  const setActiveCanvas = useSetActiveCanvas();

  useDropHandler({
    onDrop: () => {
      dispatch(setCanvasInteractionMode("edit"));
    },
    componentIdToDrag,
    componentNodeToDrag,
  });

  const showDraftComponentsControls = useShowDraftComponentsControls();
  const shouldHideCandidateOutline =
    draftComponentPosition &&
    currentDragType === "draftComponent" &&
    !isMixedStyleValue(draftComponentPosition) &&
    ["fixed", "absolute"].includes(draftComponentPosition);
  const isDragging = interactionMode === "dragging-components";
  const isActiveCanvas = canvas === activeCanvas;

  const handleCandidateBoxClick = (e: React.MouseEvent) => {
    const { shiftKey } = e;
    const storeState = store.getState();
    const componentDataMapping = selectComponentDataMapping(storeState);
    const draftComponentIds = selectDraftComponentIds(storeState);
    const leftBarActiveTab = selectLeftBarActiveTab(storeState);
    const draftNodes = selectDraftNodes(storeState);

    if (!componentIdToDrag) {
      return;
    }

    if (!isActiveCanvas && shiftKey) {
      infoToast(
        "Cannot select multiple components across difference device sizes",
        "",
      );
      return;
    }
    setDraftElement({
      componentIds: getSelectedComponentIds(
        componentIdToDrag,
        draftComponentIds,
        componentDataMapping,
        {
          event: e,
          source: "canvas",
        },
      ),
      draftNodes: candidateNode ? [...draftNodes, candidateNode] : undefined,
    });

    setWasCandidateBoxJustClicked();
    if (leftBarActiveTab !== null) {
      dispatch(setLeftBarActiveTab("tree"));
    }
    if (!isActiveCanvas) {
      setActiveCanvas({ canvas, source: "component" });
    }
    // NOTE (Gabe 2025-04-09): setCandidateNodeFromEvent here ensures we
    // can click down the tree without having to move the mouse.
    setCandidateNodeFromEvent(e.nativeEvent);
  };

  return (
    <div
      className="relative h-full w-full"
      data-testid={`bounding-boxes-${canvas}`}
    >
      <TargetFrameOverlay />

      <div ref={targetFrameOverlayRef} className="absolute inset-0">
        <GenerativeAIBoundingBox />
        {editorMode === "edit" && (
          <>
            <DropTargetBox />
            <DraftBoxes
              offsetParentRef={targetFrameOverlayRef}
              onDragStart={() => {
                if (!isDragging) {
                  dispatch(setCanvasInteractionMode("dragging-components"));
                }
              }}
            />
            {shouldShowCandidateBox &&
              candidateCanvas === canvas &&
              componentNodeToDrag && (
                <DraggableComponentBox
                  componentNode={componentNodeToDrag}
                  componentId={componentIdToDrag}
                  offsetParentRef={targetFrameOverlayRef}
                  onClick={handleCandidateBoxClick}
                  onDrag={() => {
                    dispatch(setCanvasInteractionMode("dragging-components"));
                  }}
                />
              )}
            {showDraftComponentsControls && isActiveCanvas && (
              <>
                <ParentBox />
                <ChildBoxes />
                <AncestorWithPropsBox />
                {candidateNode &&
                  Boolean(currentDragType) &&
                  !shouldHideCandidateOutline && (
                    <BoundingBox
                      showPaddingMargin
                      showOutline={currentDragType !== "section"}
                      node={candidateNode}
                      className={twMerge(
                        currentDragType !== "section" &&
                          // eslint-disable-next-line replo/no-tailwind-literal-color
                          "border-pink-300 border-2",
                      )}
                    />
                  )}
                {draftComponent && !isDraggingSpacingIndicators && (
                  <DraftComponentControls />
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const BoundingBox: React.FC<{
  node: Element;
  showPaddingMargin: boolean;
  showOutline: boolean;
  isConnectedToDynamicData?: boolean;
  className?: string;
  style?: React.CSSProperties;
}> = ({
  node,
  showPaddingMargin,
  showOutline,
  className,
  style,
  isConnectedToDynamicData,
}) => {
  const boundingBoxStyles = useBoundingBox(node);

  if (
    !boundingBoxStyles ||
    (boundingBoxStyles.width === 0 && boundingBoxStyles.height === 0)
  ) {
    return null;
  }

  return (
    <>
      {showPaddingMargin && <SpacingIndicators node={node} />}
      <div
        className={twMerge(
          "border-0.5 absolute z-50",
          showOutline && "border-primary border-[2px]",
          isConnectedToDynamicData && "border-purple",
          className,
        )}
        style={{ ...boundingBoxStyles, pointerEvents: "none", ...style }}
      />
    </>
  );
};

const GenerativeAIBoundingBox: React.FC = () => {
  const { canvas } = useRequiredContext(CanvasContext);
  const { generationMode, status } = useV1AIStreaming();

  const draftComponentsNodes = useEditorSelector(
    (state) => selectDraftComponentsNode(state, canvas),
    shallowEqual,
  );

  if (!draftComponentsNodes) {
    return null;
  }

  const allNodes = draftComponentsNodes.map((node) => {
    if (!node) {
      return null;
    }
    return (
      <div key={node.id}>
        {generationMode === "template" && status !== "preGeneration" && (
          <BoundingBox
            node={node}
            showOutline={false}
            showPaddingMargin={false}
            style={{ zIndex: CANVAS_Z_INDICES.draftBox }}
            className={twMerge(
              status !== "finishedGenerating"
                ? "animated-gradient-border"
                : "border-primary border-2",
            )}
          />
        )}
      </div>
    );
  });

  return allNodes;
};

const ParentBox: React.FC = () => {
  const { canvas } = useRequiredContext(CanvasContext);
  const isSelfOrAncestorModal = useEditorSelector((state) => {
    return selectHasDraftComponentAncestorOrSelfWithSpecificType(
      state,
      "modal",
    );
  });
  const parentComponentNode = useEditorSelector((state) => {
    return selectDraftParentComponentNode(state, canvas);
  });

  if (isSelfOrAncestorModal || !parentComponentNode) {
    return null;
  }
  return (
    <BoundingBox
      showOutline
      showPaddingMargin={false}
      // eslint-disable-next-line replo/no-tailwind-literal-color
      className="parent-box border-blue-400 z-30 border border-dashed"
      node={parentComponentNode}
    />
  );
};

const ChildBoxes: React.FC = () => {
  const draftComponentId = useEditorSelector(selectDraftComponentId);
  const componentMapping = useEditorSelector(selectComponentMapping);
  const getAttribute = useEditorSelector(selectGetAttribute);

  const childBoxRenderComponentIds = getChildBoxRenderComponentIds(
    componentMapping,
    draftComponentId,
    getAttribute,
  );

  return childBoxRenderComponentIds.map((componentId) => {
    return <SingleChildBox key={componentId} componentId={componentId} />;
  });
};

type DraftBoxesProps = {
  offsetParentRef: React.RefObject<HTMLDivElement>;
  onDragStart(): void;
};

const DraftBoxes: React.FC<DraftBoxesProps> = ({
  onDragStart,
  offsetParentRef,
}) => {
  const draftComponentIds = useEditorSelector(selectDraftComponentIds);
  return draftComponentIds.map((draftComponentId) => {
    return (
      <DraftComponentDraftBoxes
        key={draftComponentId}
        draftComponentId={draftComponentId}
        onDragStart={onDragStart}
        offsetParentRef={offsetParentRef}
      />
    );
  });
};

const DraftComponentDraftBoxes: React.FC<
  DraftBoxesProps & { draftComponentId: string }
> = ({ onDragStart, offsetParentRef, draftComponentId }) => {
  const { canvas } = useRequiredContext(CanvasContext);

  // NOTE (Patrick, 2026-03-03, USE-2374): selectComponentNodes queries the DOM
  // via createSelector. For newly added components, the DOM node doesn't exist
  // yet during the render phase, so the selector caches an empty result. Bumping
  // this version after DOM commit invalidates the cache so the selector
  // re-queries and finds the newly rendered node — before the browser paints, so
  // there's no visual flicker.
  const [nodeQueryVersion, setNodeQueryVersion] = React.useState(0);
  // biome-ignore lint/correctness/useExhaustiveDependencies: draftComponentId is an intentional trigger to invalidate the selector cache when the selected component changes
  React.useLayoutEffect(() => {
    setNodeQueryVersion((v) => v + 1);
  }, [draftComponentId]);

  const draftComponentNodes = useEditorSelector((state) => {
    return selectComponentNodes(
      state,
      draftComponentId,
      canvas,
      nodeQueryVersion,
    );
  });
  const activeCanvas = useEditorSelector(selectActiveCanvas);
  const draftNodes = useEditorSelector(selectDraftNodes);
  const setDraftElement = useSetDraftElement();
  const setActiveCanvas = useSetActiveCanvas();

  if (isEmpty(draftComponentNodes)) {
    return null;
  }

  const isActiveCanvas = canvas === activeCanvas;

  return draftComponentNodes.map((draftComponentNode, index) => {
    return (
      <DraftBox
        key={`${draftComponentId}-${index}`}
        draftComponentNode={draftComponentNode}
        isSelected={
          draftNodes.length === 0 || draftNodes.includes(draftComponentNode)
        }
        draggingComponentNode={draftComponentNode}
        draggingComponentId={draftComponentId}
        offsetParentRef={offsetParentRef}
        onClick={() => {
          if (!isActiveCanvas) {
            setActiveCanvas({ canvas, source: "component" });
          }

          setDraftElement({
            componentIds: draftComponentId ? [draftComponentId] : undefined,
            draftNodes: draftComponentNode ? [draftComponentNode] : undefined,
          });
        }}
        onDrag={onDragStart}
      />
    );
  });
};

const DraftBox: React.FC<{
  draftComponentNode: HTMLElement;
  isSelected: boolean;
  offsetParentRef: React.RefObject<HTMLDivElement>;
  draggingComponentNode: HTMLElement;
  draggingComponentId: string | null;
  onClick(): void;
  onDrag(): void;
}> = ({
  draftComponentNode,
  isSelected,
  offsetParentRef,
  draggingComponentNode,
  draggingComponentId,
  onClick,
  onDrag,
}) => {
  const [boxSize] = useRefreshBoxSize(draftComponentNode);
  const showDraftComponentsControls = useShowDraftComponentsControls();
  const isDraftComponentDynamic = useEditorSelector(
    selectIsDraftComponentConnectedToDynamicData,
  );

  const { bounds, paddingIndicatorStyle } = boxSize;

  // TODO (Noah, 2022-01-28): Super confusing that we look at paddingIndicatorStyle here,
  // this whole component needs to be massively cleaned up in the near future
  if (paddingIndicatorStyle.display === "none") {
    return null;
  }

  if (bounds?.width === 0 && bounds.height === 0) {
    return null;
  }

  return (
    <>
      {showDraftComponentsControls && (
        <>
          <BoundingBox
            node={draftComponentNode}
            showOutline={false}
            showPaddingMargin
            isConnectedToDynamicData={Boolean(isDraftComponentDynamic)}
            style={{ zIndex: CANVAS_Z_INDICES.draftBox }}
            className="draft-box"
          />
        </>
      )}
      {isSelected && (
        <DraggableComponentBox
          offsetParentRef={offsetParentRef}
          componentNode={draggingComponentNode}
          componentId={draggingComponentId}
          onClick={onClick}
          onDrag={onDrag}
        />
      )}
    </>
  );
};

const DropTargetBox: React.FC = () => {
  const { dropTarget } = useDropTarget();
  const { canvas } = useRequiredContext(CanvasContext);
  const dropTargetNode = React.useMemo(() => {
    if (!dropTarget?.componentId) {
      return null;
    }
    return getEditorComponentNode({
      canvas,
      componentId: dropTarget.componentId,
    });
  }, [dropTarget, canvas]);

  const styles = useDropTargetLineStyles(dropTargetNode);

  if (!dropTargetNode) {
    return null;
  }
  return (
    <div className="bg-primary absolute z-50" id="dropTarget" style={styles} />
  );
};

const DraggableComponentBox: React.FC<{
  offsetParentRef: React.RefObject<HTMLDivElement>;
  componentNode: HTMLElement;
  componentId: string | null;
  onClick(e: React.MouseEvent): void;
  onDrag(): void;
}> = ({
  offsetParentRef,
  componentNode,
  componentId,
  onClick,
  onDrag: onDragFromProps,
}) => {
  const store = useEditorStore();

  const {
    currentDragType,
    currentDragIdentifier,
    setCurrentDragTypeAndIdentifier,
  } = useCurrentDragType();
  const { dropTarget, setDropTarget } = useDropTarget();
  const [boxSize, setBoxSize] = useRefreshBoxSize(componentNode);
  const draftComponentType = useEditorSelector(selectDraftComponentType);
  const { offset, onDrop } = useDragAndDrop();
  const nodeRef = React.useRef<HTMLDivElement>(null);
  const isDraftComponent = useEditorSelector((state) => {
    return selectIsDraftComponent(state, componentId);
  });
  const isDraftComponentDynamic = useEditorSelector(
    selectIsDraftComponentConnectedToDynamicData,
  );
  const reploComponent = useEditorSelector((state) => {
    return selectComponentById(state, componentId);
  });
  const { canvas } = useRequiredContext(CanvasContext);
  const activeCanvas = useEditorSelector(selectActiveCanvas);
  const isActiveCanvas = canvas === activeCanvas;
  const { setCandidateNodeFromEvent } = useSetCandidateNode();

  const canvasInteractionMode = useEditorSelector(selectCanvasInteractionMode);
  const isDragging = canvasInteractionMode === "dragging-components";

  const onDrag = React.useCallback(
    (e: DraggableEvent) => {
      // Note (Ovishek, 2022-06-16): TouchEvent doesn't support e.clientX
      // we should return on touchEvent, b/c we don't support touch drag yet
      if (!(e instanceof MouseEvent)) {
        return;
      }

      const storeState = store.getState();
      const areMultipleComponentsSelected =
        selectAreMultipleDraftComponents(storeState);

      if (areMultipleComponentsSelected) {
        return;
      }

      const draftElement =
        selectDraftElement_warningThisWillRerenderOnEveryUpdate(storeState);
      const candidateComponent = selectCandidateComponent(storeState);

      onDragFromProps();

      // Set dragging true when the user actually starts dragging. Note: we don't
      // have to update draggable state here since react-draggable manages the offset
      // internally when drag is in progress
      if (
        currentDragType !== "draftComponent" ||
        currentDragIdentifier !== componentId
      ) {
        setCurrentDragTypeAndIdentifier("draftComponent", componentId);
      }

      const candidateNode = setCandidateNodeFromEvent(e);

      if (candidateNode && offset) {
        const state = store.getState();
        const { clientX: x, clientY: y } = e;

        const newDropTarget = processDropTargetDrag(
          {
            x,
            y,
          },
          candidateNode,
          {
            type: "component",
            component: reploComponent!,
          },
          candidateComponent,
          dropTarget,
          draftElement!,
          selectGetAttribute(state),
          selectComponentDataMapping(state),
          selectActiveCanvas(state),
          currentDragType,
        );

        if (newDropTarget) {
          setDropTarget(newDropTarget);
        }
      }
    },
    [
      reploComponent,
      currentDragIdentifier,
      currentDragType,
      setCandidateNodeFromEvent,
      setCurrentDragTypeAndIdentifier,
      setDropTarget,
      onDragFromProps,
      store,
      dropTarget,
      componentId,
      offset,
    ],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: Disable exhaustive deps for now
  const onDragStop = React.useCallback(
    (data: Position) => {
      const actions: UseApplyComponentActionType[] = [];
      const nonStaticPositions = ["absolute", "fixed"];

      const deltaY = data.y - boxSize.initialPosition!.y;
      const deltaX = data.x - boxSize.initialPosition!.x;
      if (
        nonStaticPositions.includes(getComputedStyle(componentNode)?.position)
      ) {
        const updatedValue = (attribute: BoxSide) => {
          // TODO (Noah, 2021-02-01): Very weird to be using the paddingIndicatorStyle
          // here, but that's currently the style which is used to figure out this
          // component's top/left/bottom/right positioning. We need to refactor this
          // away entirely in the future
          if (!boxSize.paddingIndicatorStyle[attribute]) {
            return null;
          }
          const currentStringValue = `${boxSize.paddingIndicatorStyle[attribute] ?? 0}`;
          if (!currentStringValue) {
            return null;
          }
          const unitValue = parseUnit(
            currentStringValue,
            { value: "0", unit: "px" },
            "",
            "px",
          );
          let width = "",
            height = "";
          if (componentNode.parentElement != null) {
            const style = getComputedStyle(componentNode.parentElement);
            width = style.width;
            height = style.height;
          }
          const isFixedToPage =
            getComputedStyle(componentNode)?.position === "fixed";
          let numberHeight = Number.parseFloat(height.replace("px", ""));
          let numberWidth = Number.parseFloat(width.replace("px", ""));
          if (isFixedToPage && offset) {
            numberHeight = offset.canvasHeight;
            numberWidth = offset.canvasWidth;
          }
          const value = Number.parseFloat(String(unitValue.value));
          let currentValue = Number.parseInt(
            currentStringValue.replace("px", ""),
          );
          const isPercentage = unitValue.unit === "%";
          const isVertical = attribute === "top" || attribute === "bottom";
          if (isPercentage) {
            if (isVertical) {
              currentValue = (value * numberHeight) / 100;
            } else {
              currentValue = (value * numberWidth) / 100;
            }
          }

          let newValue;
          switch (attribute) {
            case "top":
              newValue = currentValue + deltaY;
              break;
            case "bottom":
              newValue = currentValue - deltaY;
              break;
            case "left":
              newValue = currentValue + deltaX;
              break;
            case "right":
              newValue = currentValue - deltaX;
              break;
            default:
              newValue = currentValue;
              break;
          }
          if (isPercentage) {
            if (isVertical) {
              return `${((newValue / numberHeight) * 100).toFixed(2)}%`;
            }
            return `${((newValue / numberWidth) * 100).toFixed(2)}%`;
          }
          return `${newValue}px`;
        };
        const newStyles: Record<string, any> = {};
        for (const attribute of ["top", "left", "bottom", "right"]) {
          const newValue = updatedValue(
            attribute as "top" | "bottom" | "left" | "right",
          );
          if (newValue) {
            newStyles[attribute] = newValue;
          }
        }
        if (!isEmpty(newStyles)) {
          actions.push({
            type: "setStyles",
            value: newStyles,
          });
        }

        setBoxSize((prevBoxSize) => {
          const draggable = prevBoxSize.draggable;
          const x = Number(data.x);
          const y = Number(data.y);
          if (draggable?.position.x === x && draggable.position.y === y) {
            return prevBoxSize;
          }
          return {
            ...prevBoxSize,
            draggable: {
              position: { x, y },
            },
          };
        });
      } else {
        setBoxSize((prevBoxSize) => {
          return {
            ...prevBoxSize,
            draggable: {
              position: boxSize.initialPosition!,
            },
          };
        });
      }
      onDrop?.current?.(dropTarget, null, actions);
      setDropTarget({ componentId: null, error: null, edge: null });
      setCurrentDragTypeAndIdentifier(null, null);
    },
    [
      dropTarget,
      offset,
      componentNode,
      boxSize,
      setCurrentDragTypeAndIdentifier,
    ],
  );

  const getCursor = () => {
    if (currentDragType) {
      return "grabbing";
    }

    const imageSource = selectImageSource(store.getState());
    const isBrokenImage = componentId
      ? selectEditorBrokenMediaComponentIds(store.getState()).includes(
          componentId,
        )
      : false;

    if (reploComponent?.type === "image" && (!imageSource || isBrokenImage)) {
      return "pointer";
    }

    return "default";
  };

  const cursor = getCursor();

  if (!boxSize.draggable?.position || !componentId || !reploComponent) {
    return null;
  }

  return (
    <Draggable
      axis="both"
      handle=".draggable--box"
      scale={offset?.scale ?? 1}
      defaultPosition={boxSize.initialPosition!}
      position={boxSize.draggable.position}
      onDrag={onDrag}
      onStop={(_, data) => onDragStop(data)}
      bounds={DRAGGABLE_BOUNDS}
      offsetParent={offsetParentRef?.current ?? undefined}
      nodeRef={nodeRef}
    >
      <div
        id={`draggable-box-${draftComponentType}-${componentId}`}
        data-testid={
          isDraftComponent
            ? `draggable-box-${canvas}`
            : `candidate-box-${canvas}`
        }
        ref={nodeRef}
        className={twMerge(
          "draggable--box absolute",
          currentDragType !== "section" && [
            "border-2",
            isDraftComponent ? "border-primary" : "border-amber",
            isDraftComponent && isDraftComponentDynamic && "border-purple",
            isDraftComponent &&
              (isActiveCanvas ? "border-solid" : "border-dashed"),
          ],
        )}
        onClick={(e) => {
          // NOTE (Gabe 2025-04-09): double clicking in the canvas is a special
          // case that's handled in Canvas.tsx
          if (e.detail === 1) {
            onClick(e);
            e.stopPropagation();
          }
        }}
        onContextMenu={onClick}
        style={{
          width: boxSize.bounds?.width,
          height: boxSize.bounds?.height,
          cursor,
          zIndex: CANVAS_Z_INDICES.draggableComponentBox,
        }}
      >
        {isFeatureEnabled("component-previews") && isDragging && (
          <div className="pointer-events-none flex h-full opacity-75">
            <IsolatedReploComponent
              component={reploComponent}
              variant="fullSize"
            />
          </div>
        )}
      </div>
    </Draggable>
  );
};

const SingleChildBox: React.FC<{ componentId: string }> = ({ componentId }) => {
  const draftComponentId = useEditorSelector(selectDraftComponentId);
  const { currentDragType } = useCurrentDragType();
  const { canvas } = useRequiredContext(CanvasContext);

  const isDraftComponent = componentId === draftComponentId;

  const componentNode = getEditorComponentNode({
    canvas,
    componentId,
  });

  const [children, setChildren] = React.useState(
    Array.from(componentNode?.children ?? []) as HTMLElement[],
  );

  useMutationObserver(
    componentNode,
    React.useCallback(() => {
      setChildren(Array.from(componentNode?.children ?? []) as HTMLElement[]);
    }, [componentNode]),
    { attributes: true, childList: true, subtree: true },
  );

  return (
    <>
      {children.map((child, index) => {
        let renderChild = child;
        if (!child?.dataset["rid"] && child.children[0]) {
          // Note (Ovishek, 2023-03-16): We want to only highlight replo components
          // things like grid wrapper / carousel intermediate elements should be ignored
          renderChild = child.children[0] as HTMLElement;
        }
        if (!renderChild?.dataset["rid"]) {
          // Note (Noah, 2023-06-13): Don't render child boxes for any elements that
          // aren't Replo components themselves
          return null;
        }
        return (
          <BoundingBox
            key={index}
            node={renderChild}
            showPaddingMargin={false}
            showOutline={currentDragType !== "section"}
            className={twMerge(
              "child-box z-1",
              currentDragType !== "section" && "border-primary border-2",
              isDraftComponent &&
                currentDragType !== "section" &&
                "border-primary",
            )}
          />
        );
      })}
    </>
  );
};

const AncestorWithPropsBox: React.FC = () => {
  const component = useEditorSelector(
    selectDraftComponentAncestorWithEditorProps,
  );
  const { currentDragType } = useCurrentDragType();
  const { canvas } = useRequiredContext(CanvasContext);
  const componentNode = useEditorSelector((state) => {
    return selectDraftComponentAncestorWithEditorPropsNode(state, canvas);
  });
  const boundingBoxStyles = useBoundingBox(componentNode ?? null);

  if (!component || !componentNode) {
    return null;
  }
  const shouldShowBorder = currentDragType !== "section";
  const isDynamicAncestor = shouldShowBorder && component.type === "product";
  return (
    <div
      className={twMerge(
        shouldShowBorder && "border-primary border-2",
        // eslint-disable-next-line replo/no-tailwind-literal-color
        isDynamicAncestor && "border-purple-300",
      )}
      style={{
        position: "absolute",
        pointerEvents: "none",
        // Note (Evan, 2024-06-17): "we can just do max - 1" -Yuxin
        zIndex: CANVAS_Z_INDICES.ancestorWithPropsBox,
        ...boundingBoxStyles,
      }}
    />
  );
};

const DraftComponentControls: React.FC = () => {
  const { canvas } = useRequiredContext(CanvasContext);
  const symbols = useEditorSelector(selectSymbolsMapping);
  const elementType = useEditorSelector(selectDraftElementType);
  const component = useEditorSelector(selectDraftComponent);
  const draftComponentHasParent = useEditorSelector(
    selectDraftComponentHasParent,
  );

  const isSelfOrAncestorTooltipContent = useEditorSelector((state) => {
    return selectHasDraftComponentAncestorOrSelfWithSpecificType(
      state,
      "tooltipContent",
    );
  });

  const draftComponentNode = useEditorSelector(
    (state) => selectDraftComponentNode(state, canvas),
    shallowEqual,
  );

  const ancestorWithEditorProps = useEditorSelector(
    selectDraftComponentAncestorWithEditorProps,
  );

  const ancestorWithEditorPropsNode = useEditorSelector(
    (state) => selectDraftComponentAncestorWithEditorPropsNode(state, canvas),
    shallowEqual,
  );

  const [
    { width: ancestorControlBoxWidth, height: ancestorControlBoxHeight },
    setDimensions,
  ] = React.useState({ width: 0, height: 0 });
  const ancestorControlBoxRef = React.useRef<HTMLDivElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies(ancestorWithEditorPropsNode): We actually want this
  React.useLayoutEffect(() => {
    if (ancestorControlBoxRef.current) {
      setDimensions({
        width: ancestorControlBoxRef.current.offsetWidth,
        height: ancestorControlBoxRef.current.offsetHeight,
      });
    }
  }, [ancestorWithEditorPropsNode]);

  const bounds = useBoundingBox(draftComponentNode);
  const ancestorBounds = useBoundingBox(ancestorWithEditorPropsNode);

  const editorMode = useEditorSelector(selectEditorMode);

  if (!component || !draftComponentNode || !elementType) {
    return null;
  }

  if (!bounds || (bounds.width === 0 && bounds.height === 0)) {
    return null;
  }

  const overlappingBounds =
    // NOTE (Sebas, 2024-07-04): We don't want to overlap the control box if the draft component is a tooltip content,
    // this is because the tooltip content is absolutely positioned and it will show the control box in the wrong place.
    !isSelfOrAncestorTooltipContent && ancestorBounds
      ? bounds.top < (ancestorBounds?.top ?? 0) + ancestorControlBoxHeight &&
        bounds.left < (ancestorBounds?.left ?? 0) + ancestorControlBoxWidth
      : false;

  const boundsTop = overlappingBounds ? ancestorBounds!.top : bounds.top;

  const ancestorLeft = ancestorBounds?.left ?? 0;
  const overlappingBoundsLeft = `calc(${ancestorLeft + (ancestorControlBoxWidth + 6)}px)`;

  const label = draftComponentHasParent
    ? sanitizeTreeContent(getComponentName(component, symbols))
    : elementTypeToEditorData[elementType].singularDisplayName;

  return (
    editorMode !== "aiGeneration" && (
      <div
        className="absolute"
        style={{
          // Note (Evan, 2024-06-17): "Call this max" -Yuxin
          // Note (Jackson, 2025-01-29): This needs to be the highest z-index out of all of the
          // interactibles on the canvas eg. bounding boxes, drag resizers, etc.
          // you were the chosen index... it was said you would bring balance to the canvas,
          // not leave it in darkness!
          zIndex: CANVAS_Z_INDICES.draftComponentControls,
        }}
        onClick={(event) => {
          // NOTE (Gabe 2025-04-10): We stop the event propagation here to
          // prevent any canvas interactions from being triggered when the
          // ControlBox (or AncestorControlBox) is clicked.
          event.stopPropagation();
        }}
      >
        <ControlBox
          component={component}
          top={`calc(${boundsTop ?? 0}px - 36px)`}
          left={overlappingBounds ? overlappingBoundsLeft : bounds.left}
          boundingBoxWidth={bounds.width}
          label={label}
        />
        {!isSelfOrAncestorTooltipContent &&
          ancestorWithEditorProps &&
          ancestorWithEditorPropsNode && (
            <AncestorControlBox
              boxRef={ancestorControlBoxRef}
              component={ancestorWithEditorProps}
              top={`calc(${ancestorBounds?.top ?? 0}px - 36px)`}
              left={ancestorLeft}
              boundingBoxWidth={ancestorBounds?.width ?? 0}
              label={sanitizeTreeContent(
                getComponentName(ancestorWithEditorProps, symbols),
              )}
            />
          )}
      </div>
    )
  );
};

const DynamicDataIconButton = ({
  component,
}: {
  component: ReploComponent;
}) => {
  const { dynamicDataProp, handleChangePropValue, handleRemovePropValue } =
    useDynamicDataQuickConnect(component);
  if (!dynamicDataProp) {
    return null;
  }
  const { propValue, targetType } = dynamicDataProp;

  if (targetType === "productOnly") {
    return null;
  }

  let path;
  if (targetType === "imageList") {
    if (propValue?.valueType === "dynamic") {
      path = propValue.dynamicPath;
    }
  } else if (typeof propValue === "string") {
    path = propValue;
  }
  return (
    <DynamicDataSelector
      side="top"
      sideOffset={10}
      onChange={handleChangePropValue}
      onRemove={handleRemovePropValue}
      targetType={targetType}
      location="canvas"
      initialPath={getPathFromVariable(path ?? "")}
      trigger={
        <div className="flex items-center border-l border-white/25">
          <DeprecatedIconButton
            icon={<Database size={16} />}
            variant="primary"
            tooltipText="Connect Data"
            style={{
              backgroundColor: "transparent",
              padding: 0,
            }}
          />
        </div>
      }
    />
  );
};

const DynamicDataFormatterIconButton = () => {
  const applyComponentAction = useApplyComponentAction();
  const dynamicDataInfoList = useEditorSelector(
    selectDraftComponentTextDynamicDataInfo,
  );
  const dynamicDataInfo = dynamicDataInfoList?.[0];
  const formatters = dynamicDataInfo?.formatters;
  const referenceType =
    dynamicDataInfo?.path && getDynamicDataReferenceType(dynamicDataInfo.path);
  const sellingPlans = useEditorSelector(selectDraftComponentSellingPlans);
  const handleChange = (newValue: string) => {
    applyComponentAction({
      type: "setProps",
      value: {
        text: newValue,
      },
    });
  };
  if (!referenceType) {
    return null;
  }

  return (
    <DynamicDataFormatter
      side="top"
      sideOffset={10}
      initialValue={formatters ?? []}
      referenceType={referenceType}
      location="canvas"
      onChange={handleChange}
      sellingPlans={sellingPlans}
      trigger={
        <div className="flex items-center border-l border-white/25">
          <DeprecatedIconButton
            icon={<Hash size={16} />}
            variant="primary"
            tooltipText="Format Data"
            style={{
              backgroundColor: "transparent",
              padding: 0,
            }}
          />
        </div>
      }
    />
  );
};

type ControlBoxProps = {
  component: ReploComponent;
  top: string | number;
  left: string | number;
  label: string;
  boundingBoxWidth: number;
};

const ControlBox: React.FC<ControlBoxProps> = ({
  component,
  top,
  left,
  boundingBoxWidth,
  label,
}) => {
  const hasCustomProps = getCustomPropDefinitions(component).some(
    (def) => def.type !== "component",
  );
  const editorProps = getRenderData(component.type)?.editorProps;
  const scale = useEditorSelector(selectCanvasScale);
  const dispatch = useEditorDispatch();
  const supportsDynamic = doesComponentSupportDynamicDataProps(component);
  const isDynamic = doesComponentHaveConnectedDynamicDataProps(component);
  const transformedScale = scalingFactor(scale);
  return (
    <div
      className="pointer-events-none absolute flex items-center justify-between"
      style={{
        top,
        left,
        width:
          boundingBoxWidth && transformedScale < 1
            ? boundingBoxWidth * scale
            : boundingBoxWidth,
        // Note (Evan, 2024-05-09): Mild hack: the translateZ(0) and backfaceVisibility: "hidden"
        // are both tricks to get the text to not be blurry when scaled, see: https://stackoverflow.com/questions/14677490/blurry-text-after-using-css-transform-scale-in-chrome
        transform: `scale(${transformedScale}) translateZ(0) `,
        transformOrigin: "bottom left",
        backfaceVisibility: "hidden",
      }}
    >
      <div
        className={`h-[${CONTROL_BOX_HEIGHT}]px pointer-events-auto flex flex-row items-stretch gap-2`}
      >
        <div
          className={twMerge(
            "bg-primary py-1/2 flex flex-row items-center justify-start rounded-md pl-2 text-sm font-medium leading-7 text-white",
            isDynamic && "bg-purple",
          )}
        >
          <span className="mr-2 max-w-[200px] truncate font-[1rem]">
            {label}
          </span>
          {supportsDynamic && <DynamicDataIconButton component={component} />}
          {supportsDynamic && <DynamicDataFormatterIconButton />}
          {hasCustomProps && (
            <div className="flex items-center border-l border-white/25">
              <DeprecatedIconButton
                icon={<Settings size={16} />}
                variant="primary"
                onClick={() => {
                  dispatch(setRightBarActiveTab("custom"));
                }}
                tooltipText="Config"
                style={{
                  backgroundColor: "transparent",
                  padding: 0,
                }}
              />
            </div>
          )}
          {editorProps &&
            Object.entries(editorProps).map(([id, definition]) => (
              <EditorPropControl
                key={id}
                component={component}
                id={id}
                propDefinition={definition}
              />
            ))}
        </div>
      </div>
      <BoundingBoxPropControls component={component} />
    </div>
  );
};

const AncestorControlBox: React.FC<
  ControlBoxProps & {
    boxRef: React.RefObject<HTMLDivElement>;
  }
> = ({ boxRef, component, top, left, boundingBoxWidth, label }) => {
  const hasCustomProps = getCustomPropDefinitions(component).some(
    (def) => def.type !== "component",
  );
  const editorProps = getRenderData(component.type)?.editorProps;
  const dispatch = useEditorDispatch();
  const setDraftElement = useSetDraftElement();
  const scale = useEditorSelector(selectCanvasScale);
  const isDynamic = doesComponentHaveConnectedDynamicDataProps(component);
  const transformedScale = scalingFactor(scale);
  return (
    <div
      className="pointer-events-none absolute flex items-center justify-between"
      style={{
        top,
        left,
        width:
          boundingBoxWidth && transformedScale < 1
            ? boundingBoxWidth * scale
            : boundingBoxWidth,
        // Note (Evan, 2024-05-09): Mild hack: the translateZ(0) and backfaceVisibility: "hidden"
        // are both tricks to get the text to not be blurry when scaled, see: https://stackoverflow.com/questions/14677490/blurry-text-after-using-css-transform-scale-in-chrome
        transform: `scale(${transformedScale}) translateZ(0) `,
        transformOrigin: "bottom left",
        backfaceVisibility: "hidden",
      }}
    >
      <div ref={boxRef} className="pointer-events-auto">
        <div
          className={twMerge(
            "bg-primary py-1/2 flex flex-row items-center justify-start rounded-md px-2 text-sm font-medium leading-7 text-white",
            // eslint-disable-next-line replo/no-tailwind-literal-color
            isDynamic && "bg-purple-300",
          )}
        >
          <button
            className="mr-2 max-w-[200px] truncate font-[1rem]"
            onClick={() => {
              setDraftElement({
                componentIds: [component.id],
              });
            }}
          >
            {label}
          </button>
          {hasCustomProps && (
            <div className="flex items-center border-l border-white/25">
              <DeprecatedIconButton
                icon={<Settings size={16} />}
                variant="primary"
                onClick={() => {
                  setDraftElement({
                    componentIds: [component.id],
                  });

                  setTimeout(() => {
                    dispatch(setRightBarActiveTab("custom"));
                  });
                }}
                tooltipText="Config"
                style={{
                  backgroundColor: "transparent",
                  padding: 0,
                }}
              />
            </div>
          )}
          {editorProps &&
            Object.entries(editorProps).map(([id, definition]) => (
              <EditorPropControl
                key={id}
                component={component}
                id={id}
                propDefinition={definition}
              />
            ))}
        </div>
      </div>
      <BoundingBoxPropControls component={component} />
    </div>
  );
};

const EditorPropControl: React.FC<{
  id: string;
  component: ReploComponent;
  propDefinition: EditorProp;
}> = ({ id, propDefinition: { type, description }, component }) => {
  const draftElementId = useEditorSelector(selectDraftElementId);
  const { canvas } = useRequiredContext(CanvasContext);

  const accordionAncestor = useEditorSelector(
    selectDraftComponentAccordionAncestor,
  );

  // TODO (Gabe 2025-04-15): It is not great that we have to understand what
  // type of component we are operating on in the EditorPropControl. It's also
  // not great that the logic for determining a shared state key exists both
  // here and in set-draft-element.ts. This is the kind of thing that should be
  // moved into ComponentContext in the future.
  const sharedKey = (() => {
    if (component.type === "collapsibleV2" && accordionAncestor) {
      return getSharedStateKey(accordionAncestor, "accordionOpenItems");
    } else {
      return getSharedStateKey(component, id);
    }
  })();

  const [currentValue, setValue] = usePaintSharedStateValue(sharedKey);

  const editorData = getEditorPropEditorData(type);
  const dispatch = useEditorDispatch();

  const onChange = (value: any) => {
    if (!draftElementId) {
      return;
    }

    if (type === "collapsibility" && Boolean(accordionAncestor)) {
      const newAccordionOpenItems = getAccordionNewItems(
        component.id,
        currentValue as any,
        false,
      );
      setValue(newAccordionOpenItems);
    } else if (type === "interactions") {
      dispatch(setRightBarActiveTab("interactions"));
    } else {
      setValue(value);
    }
    if (
      component.type === "carouselV3" &&
      type === "index" &&
      typeof value === "number"
    ) {
      const result = getComponentIdFromSlideIndex(canvas, component, value);
      if (result) {
        dispatch(
          setDraftElement({
            componentIds: result.componentId ? [result.componentId] : undefined,
          }),
        );
      }
    }
  };

  const shouldShowEditorControl = canRenderEditorControl(type, component);
  if (!shouldShowEditorControl) {
    return null;
  }

  return (
    <div className="border-white/22 flex items-center border-l font-[1rem]">
      {editorData?.render({
        value: currentValue ?? editorData?.defaultValue,
        id,
        component,
        onChange,
        description,
        componentId: component.id,
      })}
    </div>
  );
};

const TargetFrameOverlay: React.FC = () => {
  const { canvas } = useRequiredContext(CanvasContext);
  return (
    <div id={`targetFrameOverlay-${canvas}`} className="absolute inset-0" />
  );
};

// #endregion

// #region Hooks
function useBoundingBox(node: Element | null | undefined) {
  const getCanvasRelativeBoundingClientRect = useEditorSelector(
    selectCanvasRelativeBoundingClientRect,
  );
  const {
    top = 0,
    left = 0,
    width = 0,
    height = 0,
  } = node ? getCanvasRelativeBoundingClientRect(node) : {};

  const [boundingBox, setBoundingBox] = React.useState({
    top,
    left,
    width,
    height,
  });

  const updateBoundingBox = React.useCallback(() => {
    setBoundingBox((previousBoundingBox) => {
      if (!node) {
        return previousBoundingBox;
      }
      const { top, left, width, height } =
        getCanvasRelativeBoundingClientRect(node);
      const newBoundingBox = {
        top,
        left,
        width,
        height,
      };

      return isEqualBoundingBox(newBoundingBox, previousBoundingBox)
        ? previousBoundingBox
        : newBoundingBox;
    });
  }, [node, getCanvasRelativeBoundingClientRect]);

  useMutationObserver(node, updateBoundingBox, {
    subtree: true,
    attributes: true,
    childList: true,
  });

  // NOTE (Patrick, 2026-02-26, REPL-24453): Right-panel style changes are applied via
  // stylesheet injection (usePaintTargetFrames writes to a <style> tag in document.head),
  // so the MutationObserver on the node doesn't catch them. We observe the editor
  // stylesheet element directly to detect all style changes (size, position, etc.).
  React.useEffect(() => {
    const styleEl = document.head.querySelector("[data-replo-editor-style]");
    if (!styleEl) {
      return;
    }
    const observer = new MutationObserver(() => {
      updateBoundingBox();
    });
    observer.observe(styleEl, {
      characterData: true,
      childList: true,
      subtree: true,
    });
    return () => observer.disconnect();
  }, [updateBoundingBox]);

  if (!node) {
    return null;
  }

  return boundingBox;
}

function useDropTargetLineStyles(
  dropTargetNode: HTMLElement | null,
): React.CSSProperties {
  const draftElement = useEditorSelector(
    selectDraftElement_warningThisWillRerenderOnEveryUpdate,
  );
  const componentMapping = useEditorSelector(selectComponentMapping);
  const store = useEditorStore();

  const { dropTarget } = useDropTarget();
  const { canvas } = useRequiredContext(CanvasContext);

  const getMargin = (position: string, node: HTMLElement) => {
    return getElementMargin(position, node);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: Disable exhaustive deps for now
  const getDropTargetStyles = React.useCallback((): React.CSSProperties => {
    if (!dropTargetNode || !dropTarget?.edge || !draftElement) {
      return {};
    }

    const storeState = store.getState();
    const style: React.CSSProperties = {};

    const componentId = dropTarget.componentId;
    const targetEdge = dropTarget.edge;

    const getCanvasRelativeBoundingClientRect =
      selectCanvasRelativeBoundingClientRect(storeState);

    const { top, left, bottom, right, width, height } =
      getCanvasRelativeBoundingClientRect(dropTargetNode);

    const isEmptyDraftElement =
      draftElement.component.children &&
      draftElement.component.children.length === 0;

    if (isEmptyDraftElement) {
      return {
        top,
        left,
        height,
        width,
        backgroundColor: "var(--Primitives-blue-50, #EFF6FF)",
        border: "1px solid var(--Primitives-blue-600, #2563EB)",
        borderRadius: "4px",
        zIndex: CANVAS_Z_INDICES.draftBox,
      };
    }

    const lineWidth = 5;

    style.opacity = `${targetEdge === "inside" ? 0.2 : 1}`;

    const getAttribute = selectGetAttribute(storeState);
    const parent = getParentComponentFromMapping(componentMapping, componentId);
    const isValidSibling = (component: ReploComponent) => {
      const displayValue = getAttribute(
        component,
        "style.display",
        null,
      )?.value;
      const displayIsValid = !displayValue || displayValue !== "none";

      return component.type !== "modal" && displayIsValid;
    };
    const sibling =
      targetEdge === "top" || targetEdge === "left"
        ? findPreviousSibling(draftElement, componentId, isValidSibling)
        : findNextSibling(draftElement, componentId, isValidSibling);

    const getMargins = (node: HTMLElement): Record<string, number> => {
      return Object.fromEntries(
        ["top", "bottom", "left", "right"].map((position) => [
          position,
          Number.parseInt(getMargin(position, node), 10),
        ]),
      );
    };

    const getStyles = (): React.CSSProperties => {
      const margin = getMargins(dropTargetNode);

      const isTargetVertical =
        getAttribute(parent, "style.flexDirection").value === "column";

      const siblingNode = sibling
        ? getEditorComponentNode({
            canvas,
            componentId: sibling.id,
          })
        : null;

      if (targetEdge === "top" || targetEdge === "bottom") {
        if (isTargetVertical && siblingNode) {
          const siblingRect = getCanvasRelativeBoundingClientRect(siblingNode);
          const siblingMargin = getMargins(siblingNode);

          const mid =
            (targetEdge === "top"
              ? siblingRect.bottom + siblingMargin.bottom! + top - margin.top!
              : siblingRect.top -
                siblingMargin.top! +
                bottom +
                margin.bottom!) / 2;

          return {
            top: mid - lineWidth / 2,
            left: Math.min(left, siblingRect.left),
            height: lineWidth,
            width: Math.max(width, siblingRect.width),
          };
        }

        return {
          top:
            targetEdge === "top"
              ? top - margin.top!
              : bottom - lineWidth + margin.bottom!,
          left,
          height: lineWidth,
          width,
        };
      }

      if (targetEdge === "left" || targetEdge === "right") {
        if (!isTargetVertical && siblingNode) {
          const siblingRect = getCanvasRelativeBoundingClientRect(siblingNode);
          const siblingMargin = getMargins(siblingNode);

          const mid =
            (targetEdge === "left"
              ? siblingRect.right + siblingMargin.right! + left - margin.left!
              : siblingRect.left -
                siblingMargin.left! +
                right +
                margin.right!) / 2;

          return {
            top: Math.min(top, siblingRect.top),
            left: mid - lineWidth / 2,
            height: Math.max(height, siblingRect.height),
            width: lineWidth,
          };
        }

        return {
          top,
          left:
            targetEdge === "left"
              ? left - margin.left!
              : right - lineWidth + margin.right!,
          height,
          width: lineWidth,
        };
      }

      if (targetEdge === "inside") {
        return {
          top,
          left,
          height,
          width,
        };
      }

      return {};
    };

    const styles = getStyles();
    if (styles) {
      style.top = `${styles.top}px`;
      style.left = `${styles.left}px`;
      style.height = `${styles.height}px`;
      style.width = `${styles.width}px`;
    }

    return style;
  }, [dropTargetNode, dropTarget, draftElement, store]);

  return getDropTargetStyles();
}

type RefreshBoxSizeState = {
  bounds: NumericBounds | null;
  margins: Edges | null;
  paddings: Edges | null;
  borders: Edges | null;
  paddingIndicatorStyle: React.CSSProperties;
  draggable: {
    position: Position;
  } | null;
  initialPosition: Position | null;
};

function useRefreshBoxSize(
  componentNode: HTMLElement,
): [
  state: RefreshBoxSizeState,
  setState: React.Dispatch<React.SetStateAction<RefreshBoxSizeState>>,
] {
  const [boxSize, setBoxSize] = React.useState<RefreshBoxSizeState>({
    paddingIndicatorStyle: { display: "none" },
    bounds: null,
    margins: null,
    paddings: null,
    borders: null,
    draggable: null,
    initialPosition: null,
  });
  const draftComponent = useEditorSelector(selectDraftComponent);
  const store = useEditorStore();

  const _getEdgeAttribute = React.useCallback(
    (attribute: RuntimeStyleAttribute) => {
      const getAttribute = selectGetAttribute(store.getState());
      return getEdgeAttribute(draftComponent, attribute, getAttribute);
    },
    [store, draftComponent],
  );

  const refreshBoxSizeInternal = React.useCallback(() => {
    const getCanvasRelativeBoundingClientRect =
      selectCanvasRelativeBoundingClientRect(store.getState());
    const {
      top: boundingTop,
      left: boundingLeft,
      width: boundingWidth,
      height: boundingHeight,
    } = getCanvasRelativeBoundingClientRect(componentNode);
    const style = getComputedStyle(componentNode);

    setBoxSize((prevBoxSize) => {
      return {
        ...prevBoxSize,
        bounds: {
          top: boundingTop,
          height: boundingHeight,
          left: boundingLeft,
          width: boundingWidth,
        },
        margins: {
          top: style.marginTop,
          left: style.marginLeft,
          bottom: style.marginBottom,
          right: style.marginRight,
        },
        borders: {
          top: style.borderTopWidth,
          left: style.borderLeftWidth,
          bottom: style.borderBottomWidth,
          right: style.borderRightWidth,
        },
        paddings: {
          top: style.paddingTop,
          left: style.paddingLeft,
          bottom: style.paddingBottom,
          right: style.paddingRight,
        },
        paddingIndicatorStyle: {
          top: _getEdgeAttribute("top"),
          left: _getEdgeAttribute("left"),
          right: _getEdgeAttribute("right"),
          bottom: _getEdgeAttribute("bottom"),
        },
        initialPosition: { x: boundingLeft, y: boundingTop },
        draggable: {
          position: { x: boundingLeft, y: boundingTop },
        },
      };
    });
  }, [_getEdgeAttribute, componentNode, store]);

  useMutationObserver(componentNode, refreshBoxSizeInternal, {
    subtree: true,
    childList: true,
    attributes: true,
  });

  const boundingBox = useBoundingBox(componentNode);

  React.useEffect(() => {
    setBoxSize((prevBoxSize) => {
      return {
        ...prevBoxSize,
        bounds: Object.assign({}, prevBoxSize.bounds, boundingBox),
        initialPosition: {
          x: boundingBox?.left ?? 0,
          y: boundingBox?.top ?? 0,
        },
        draggable: {
          position: boundingBox
            ? { x: boundingBox.left, y: boundingBox.top }
            : { x: 0, y: 0 },
        },
      };
    });
  }, [boundingBox]);

  return [boxSize, setBoxSize];
}

// #endregion
